import { useCallback, useEffect, useMemo, useState } from 'react';
import { NavLink, Outlet, useLocation } from 'react-router-dom';
import { Bell, FileImage, FolderOpen, ShieldCheck, Users } from '../../shared/assets/AppIcons';
import { useSession } from '@/app/providers/session-provider';
import { getAdminRoutes } from '@/app/router/route-registry';
import { listAdminTenants } from '@/modules/admin/services/admin-tenants.service';
import '@/modules/admin/styles/control-plane.css';

const NAV_SECTIONS = [
  { id: 'platform', label: 'Piattaforma' },
  { id: 'product', label: 'Prodotto' },
  { id: 'company', label: 'Azienda' },
];

export default function AdminShell() {
  const { currentUser } = useSession();
  const location = useLocation();
  const [tenantCount, setTenantCount] = useState(null);
  const adminRoutes = getAdminRoutes().filter((route) => route.nav?.visible !== false);
  const routeCounts = useMemo(
    () => ({
      '/admin/tenants': tenantCount,
    }),
    [tenantCount]
  );

  const refreshAdminMetrics = useCallback(async () => {
    const tenantsResult = await listAdminTenants();
    if (tenantsResult.ok) {
      const realTenants = tenantsResult.data.tenants.filter((tenant) => !tenant.isPlatformRoot);
      setTenantCount(realTenants.length);
      return;
    }
    setTenantCount(null);
  }, []);

  const adminMetrics = useMemo(
    () => ({
      tenantCount,
      setTenantCount,
      refreshAdminMetrics,
    }),
    [refreshAdminMetrics, tenantCount]
  );

  useEffect(() => {
    let active = true;

    async function loadSidebarState() {
      const tenantsResult = await listAdminTenants();

      if (!active) {
        return;
      }

      if (tenantsResult.ok) {
        const realTenants = tenantsResult.data.tenants.filter((tenant) => !tenant.isPlatformRoot);
        setTenantCount(realTenants.length);
      } else {
        setTenantCount(null);
      }
    }

    loadSidebarState();

    return () => {
      active = false;
    };
  }, []);

  function getRouteIcon(routeId) {
    if (routeId === 'admin-tenants') {
      return ShieldCheck;
    }
    if (routeId === 'admin-control-plane-clients') {
      return Users;
    }
    if (routeId === 'admin-repository') {
      return FolderOpen;
    }
    if (routeId === 'admin-assets') {
      return FileImage;
    }
    if (routeId === 'admin-notifications') {
      return Bell;
    }
    return ShieldCheck;
  }

  return (
    <div className="control-plane-shell">
      <aside className="control-plane-sidebar">
        <div className="control-plane-sidebar__brand">
          <div className="control-plane-sidebar__brand-badge">Platform Admin</div>
          <div className="control-plane-sidebar__brand-name">Webtika Control Plane</div>
          <div className="control-plane-sidebar__brand-role">
            {currentUser?.email || 'root@webtika.local'}
          </div>
        </div>

        <nav className="control-plane-sidebar__nav" aria-label="Control plane navigation">
          {NAV_SECTIONS.map((section) => (
            <div key={section.id} className="control-plane-sidebar__group">
              <span className="control-plane-sidebar__section-title">{section.label}</span>
              {adminRoutes
                .filter((route) => route.nav?.section === section.id)
                .map((route) => {
                  const Icon = route.icon || getRouteIcon(route.id);
                  const count = routeCounts[route.path] ?? null;
                  const isNotificationRoute = route.path === '/admin/notifications';

                  return (
                    <NavLink
                      key={route.id}
                      to={route.path}
                      className={({ isActive }) => (
                        isActive ? 'control-plane-sidebar__link is-active' : 'control-plane-sidebar__link'
                      )}
                      end={route.path === '/admin/tenants' || route.path === '/admin/clients'}
                    >
                      <Icon className="control-plane-sidebar__icon" />
                      <span>{route.label}</span>
                      {typeof count === 'number' ? (
                        <span
                          className={
                            isNotificationRoute
                              ? 'control-plane-sidebar__count is-alert'
                              : 'control-plane-sidebar__count'
                          }
                        >
                          {count}
                        </span>
                      ) : null}
                    </NavLink>
                  );
                })}
            </div>
          ))}
        </nav>

        <footer className="control-plane-sidebar__footer">v0.3-beta · SUPER_ADMIN</footer>
      </aside>

      <main className="control-plane-main">
        <Outlet context={adminMetrics} />
      </main>
    </div>
  );
}
